/**
 * The scrolling message thread.
 *
 * Renders the working draft list: user turns (right-aligned, with any image/
 * file attachments) and assistant turns (left-aligned). Each COMPLETED
 * assistant message carries a MessageActions bar (copy + thumbs feedback). The
 * in-flight assistant message (the empty one being streamed into) shows a
 * typing indicator instead of an action bar, and renders its partial text
 * incrementally — content is appended to the same node so there is no flicker
 * or remount between deltas.
 *
 * Auto-scrolls to the newest content as it streams (deterministic: keyed off
 * the streamed text length + message count, not a timer).
 */
import { ArrowSquareOutIcon } from '@phosphor-icons/react';
import { useEffect, useRef } from 'react';
import { useArtifact } from '@/features/chat/artifact/useArtifact';
import { useChat } from '@/features/chat/data-access/useChat';
import { useSessionStore } from '@/features/chat/store/sessionStore';
import type { MessageAttachment } from '@/features/chat/types';
import { Button, ScrollArea } from '@andavo/web-ui';
import { Markdown } from './Markdown';
import { MessageActions } from './MessageActions';

/**
 * "Open in panel" affordance, rendered only when this completed assistant
 * message is detected as artifact-worthy (a long procedure or code block).
 * Opens the side panel showing this message's artifact. Kept as a sibling of
 * MessageActions (not a prop on it) so the table-stakes action bar stays
 * untouched and the two PRs merge cleanly.
 */
function OpenInPanelButton({
  content,
  messageIndex,
}: {
  content: string;
  messageIndex: number;
}) {
  const artifact = useArtifact(content);
  const openArtifact = useSessionStore((s) => s.openArtifact);
  if (!artifact) return null;
  return (
    <Button
      aria-label="Open in panel"
      className="mt-1 h-7 gap-1 self-start px-2 text-content-secondary"
      data-testid="artifact-open"
      onClick={() => openArtifact(messageIndex)}
      size="sm"
      variant="ghost"
    >
      <ArrowSquareOutIcon className="size-icon-sm" />
      <span className="text-xs">Open in panel</span>
    </Button>
  );
}

function AttachmentList({ attachments }: { attachments: MessageAttachment[] }) {
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map((att, i) => {
        const isImage = att.type?.startsWith('image/');
        if (isImage && att.url) {
          return (
            <img
              alt={att.name}
              className="max-h-40 rounded-md border border-border-subtle object-cover"
              key={`${att.path}-${i}`}
              src={att.url}
            />
          );
        }
        return (
          <span
            className="rounded-md border border-border-subtle bg-surface-background px-2 py-1 font-inter text-content-secondary text-xs"
            key={`${att.path}-${i}`}
          >
            {att.name}
          </span>
        );
      })}
    </div>
  );
}

function TypingIndicator() {
  return (
    <span className="inline-flex gap-1" aria-label="Assistant is typing">
      <span className="size-1.5 animate-bounce rounded-full bg-content-secondary [animation-delay:-0.3s]" />
      <span className="size-1.5 animate-bounce rounded-full bg-content-secondary [animation-delay:-0.15s]" />
      <span className="size-1.5 animate-bounce rounded-full bg-content-secondary" />
    </span>
  );
}

export function MessageThread() {
  const { messages, isStreaming } = useChat();
  const ragChunks = useSessionStore((s) => s.ragChunks);
  // Subscribe to the panel geometry so we can re-anchor the bottom when the
  // transcript reflows (docking the panel / resizing it narrows the thread and
  // re-wraps markdown). (Per the 2026-06-20 codex review, finding #4.)
  const artifactOpen = useSessionStore((s) => s.artifactPanel.open);
  const artifactWidth = useSessionStore((s) => s.artifactPanel.width);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Length of the last (streaming) assistant message drives the scroll effect
  // so it fires on every delta append, deterministically.
  const lastLen = messages[messages.length - 1]?.content.length ?? 0;
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length, lastLen]);

  // When the panel opens/closes or is resized, the thread column reflows. If the
  // user was already pinned near the bottom, keep them there (re-anchor) so the
  // reflow doesn't strand them mid-transcript. If they had scrolled up to read
  // history, leave their position alone.
  useEffect(() => {
    const sentinel = bottomRef.current;
    if (!sentinel) return;
    // The Nomad ScrollArea viewport is the nearest scrollable ancestor.
    const viewport = sentinel.closest<HTMLElement>(
      '[data-radix-scroll-area-viewport]',
    );
    if (!viewport) return;
    const distanceFromBottom =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
    if (distanceFromBottom < 120) {
      sentinel.scrollIntoView({ block: 'end' });
    }
  }, [artifactOpen, artifactWidth]);

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="font-inter text-content-secondary text-sm">
          Ask the Andavo Experts knowledge base anything.
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="flex-1">
      <div className="mx-auto flex max-w-3xl flex-col gap-4 p-6">
        {messages.map((m, i) => {
          const isLast = i === messages.length - 1;
          const isUser = m.role === 'user';
          const isStreamingThis = isLast && !isUser && isStreaming;
          return (
            // Append-only thread, never reordered within a turn → positional
            // identity is stable, so an index-based key is correct here.
            <div
              className={isUser ? 'flex justify-end' : 'flex flex-col'}
              key={`${m.role}-${i}`}
            >
              <div
                // data-testid="chat-message" — the message bubbles the e2e
                // chat/feedback specs count and read text from (selectors.ts
                // chatMessages). Playwright's getByTestId is an EXACT match on the
                // attribute value, so this stays the bare "chat-message" token; the
                // user/assistant distinction rides on a separate data attribute
                // (the e2e suite's chatMessageUser/Assistant testids are forward-
                // declared but not yet located on — keeping this exact avoids
                // breaking the count assertions).
                data-testid="chat-message"
                data-message-role={isUser ? 'user' : 'assistant'}
                className={
                  isUser
                    ? 'max-w-[80%] whitespace-pre-wrap rounded-md bg-button-primary px-4 py-2 font-inter text-content-primary-inverted text-sm'
                    : 'max-w-[80%] rounded-md bg-surface-foreground px-4 py-2 font-inter text-content-primary text-sm'
                }
              >
                {/* User turns are shown verbatim (they typed plain text, so
                    pre-wrap preserves their newlines). Assistant turns are
                    rendered as sanitized markdown — the model emits **bold**,
                    lists, links, tables, code fences, etc. Streaming partials
                    render through the same path; react-markdown tolerates
                    incomplete markdown mid-stream. */}
                {m.content ? (
                  isUser ? (
                    m.content
                  ) : (
                    <Markdown
                      citations={m.citations}
                      hasUnsourcedClaim={m.hasUnsourcedClaim}
                    >
                      {m.content}
                    </Markdown>
                  )
                ) : isStreamingThis ? (
                  <TypingIndicator />
                ) : (
                  '…'
                )}
                {isUser && m.attachments && m.attachments.length > 0 && (
                  <AttachmentList attachments={m.attachments} />
                )}
              </div>
              {!isUser && m.content && !isStreamingThis && (
                <MessageActions
                  content={m.content}
                  messageIndex={i}
                  // Regenerate is only offered on the LAST assistant turn (it
                  // re-streams from the prior user message, replacing this reply).
                  isLastAssistant={isLast}
                />
              )}
              {!isUser && m.content && !isStreamingThis && (
                <OpenInPanelButton content={m.content} messageIndex={i} />
              )}
            </div>
          );
        })}

        {ragChunks.length > 0 && (
          <details className="rounded-md border border-border-subtle bg-surface-foreground p-3">
            <summary className="cursor-pointer font-inter text-content-secondary text-xs">
              {ragChunks.length} knowledge-base source
              {ragChunks.length === 1 ? '' : 's'}
            </summary>
            <ul className="mt-2 flex flex-col gap-1">
              {ragChunks.map((c, i) => (
                <li
                  className="font-inter text-content-secondary text-xs"
                  key={c.id ?? `chunk-${i}`}
                >
                  {c.title || c.source || c.id || `Source ${i + 1}`}
                </li>
              ))}
            </ul>
          </details>
        )}

        <div ref={bottomRef} />
      </div>
    </ScrollArea>
  );
}
